import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import {
  Accordion,
  AccordionItem,
  Banner,
  BodyText,
  Button,
  FieldText,
  FieldTextarea,
  Flex,
  Form,
  Modal,
  ScrollableArea,
  Stack,
} from "@knowtex-ai/components";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { parseBstrFile, validateMacro } from "./utils/bstrParser";
import sendServerLogging from "../../../utils/sendServerLogging";
import { logInfo } from "../../../lib/sentryLogging";
import userJSON from "../../../utils/userJSON";
import BoosterIssuesModal from "./BoosterIssuesModal";
import { getSessionToken } from "../../../lib/sessionToken";

export default function UploadMacrosPanel({ userId }) {
  const [parsedMacros, setParsedMacros] = useState([]);
  const [issuesMacros, setIssuesMacros] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState(null);

  const [macroEntries, setMacroEntries] = useState([
    { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
  ]);
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const [manualSubmitStatus, setManualSubmitStatus] = useState({ type: "", message: "" });

  const handleManualInputChange = (id, field, value) => {
    setMacroEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        if (field === "dotPhrase" && value && !value.startsWith(".")) {
          value = "." + value;
        }
        return { ...entry, [field]: value };
      })
    );
  };

  const addAnotherMacro = () => {
    setMacroEntries((prev) => [
      ...prev,
      { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
    ]);
  };

  const removeMacro = (id) => {
    if (macroEntries.length > 1) {
      setMacroEntries((prev) => prev.filter((entry) => entry.id !== id));
    }
  };

  const isManualFormValid = () =>
    macroEntries.some(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );

  const BATCH_LIMIT = 100;

  const batchUploadMacros = async (macros, source) => {
    const allFormatted = macros.map((macro) => ({
      dot_phrase: macro.dot_phrase,
      keywords: macro.keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k.length > 0),
      content: macro.content,
    }));

    const chunks = [];
    for (let i = 0; i < allFormatted.length; i += BATCH_LIMIT) {
      chunks.push(allFormatted.slice(i, i + BATCH_LIMIT));
    }

    const summary = { created: 0, duplicate: 0, invalid: 0, error: 0, requested: macros.length };
    const allResults = [];
    let httpOk = true;
    let httpFailed = 0;

    for (const chunk of chunks) {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/macros/batch-upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({ macros: chunk }),
        }
      );

      if (response.ok) {
        const json = await response.json();
        const data = json.data || {};
        const chunkSummary = data.summary || {};
        summary.created += chunkSummary.created || 0;
        summary.duplicate += chunkSummary.duplicate || 0;
        summary.invalid += chunkSummary.invalid || 0;
        summary.error += chunkSummary.error || 0;
        if (Array.isArray(data.results)) {
          allResults.push(...data.results);
        }
      } else {
        httpOk = false;
        httpFailed += chunk.length;
      }
    }

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "BOOSTER_MACROS_IMPORTED",
        eventData: {
          total: macros.length,
          batches: chunks.length,
          source: "batch_upload",
          summary,
        },
      },
    });
    logInfo("Macros batch uploaded", "macro.batch_uploaded", {
      source,
      batches: chunks.length,
      all_batches_ok: httpOk,
      requested: summary.requested,
      created: summary.created,
      duplicate: summary.duplicate,
      invalid: summary.invalid,
      error: summary.error,
      http_failed: httpFailed,
    });

    return { ok: httpOk, summary, results: allResults };
  };

  const handleManualSubmit = async () => {
    if (!isManualFormValid()) return;
    setIsManualSubmitting(true);
    setManualSubmitStatus({ type: "", message: "" });

    const completeMacros = macroEntries.filter(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );

    const macrosForUpload = completeMacros.map((macro) => ({
      dot_phrase: macro.dotPhrase,
      keywords: macro.keywords,
      content: macro.content,
    }));

    try {
      const result = await batchUploadMacros(macrosForUpload, "manual");

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "MANUAL_MACROS_CREATED",
          eventData: {
            count: completeMacros.length,
            dot_phrases: completeMacros.map((m) => m.dotPhrase),
          },
        },
      });

      const { summary } = result;
      if (result.ok && summary.created > 0) {
        const parts = [`Successfully uploaded ${summary.created} macro${summary.created !== 1 ? "s" : ""}!`];
        if (summary.duplicate > 0) {
          parts.push(`${summary.duplicate} duplicate${summary.duplicate !== 1 ? "s" : ""} skipped.`);
        }
        setManualSubmitStatus({
          type: "success",
          message: parts.join(" "),
        });
        setMacroEntries([
          { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
        ]);
      } else if (result.ok && summary.created === 0 && summary.duplicate > 0) {
        setManualSubmitStatus({
          type: "error",
          message: `All ${summary.duplicate} macro${summary.duplicate !== 1 ? "s" : ""} already exist. No new macros were added.`,
        });
      } else {
        setManualSubmitStatus({
          type: "error",
          message: "Failed to upload macros. Please try again.",
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { logEvent: "ERROR_CREATING_MACRO" },
      });
      console.error("Error submitting macros:", error);
      setManualSubmitStatus({
        type: "error",
        message: "An error occurred while uploading macros. Please try again.",
      });
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ERROR_CREATING_MACRO",
          eventData: { error_message: error.message.toString() },
        },
      });
    } finally {
      setIsManualSubmitting(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setParseError(null);

    const allMacros = [];

    for (const file of acceptedFiles) {
      try {
        if (file.name.toLowerCase().endsWith(".zip")) {
          const zip = await JSZip.loadAsync(file);
          const bstrEntries = Object.keys(zip.files).filter(
            (name) =>
              name.toLowerCase().endsWith(".bstr") && !zip.files[name].dir
          );

          if (bstrEntries.length === 0) {
            setParseError("No .bstr files found in the zip archive.");
            return;
          }

          for (const name of bstrEntries) {
            const content = await zip.files[name].async("string");
            allMacros.push(parseBstrFile(content, name));
          }
        } else if (file.name.toLowerCase().endsWith(".bstr")) {
          const content = await file.text();
          allMacros.push(parseBstrFile(content, file.name));
        }
      } catch (err) {
        setParseError(`Error processing ${file.name}: ${err.message}`);
        return;
      }
    }

    if (allMacros.length === 0) {
      setParseError("No valid .bstr files found.");
      return;
    }

    const valid = [];
    const withIssues = [];

    allMacros.forEach((macro) => {
      const issues = validateMacro(macro);
      if (issues.length > 0) {
        withIssues.push({ ...macro, issues });
      } else {
        valid.push(macro);
      }
    });

    if (valid.length > 0) {
      setParsedMacros(valid);
      setShowConfirmModal(true);
    }

    if (withIssues.length > 0) {
      setIssuesMacros(withIssues);
      if (valid.length === 0) {
        setShowIssuesModal(true);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
      "application/octet-stream": [".bstr"],
      "text/plain": [".bstr"],
    },
    multiple: true,
    noClick: isSubmitting,
    noDrag: isSubmitting,
  });

  const handleIssuesResolved = (fixedMacros) => {
    setIssuesMacros([]);
    setShowIssuesModal(false);
    setParsedMacros((prev) => [...prev, ...fixedMacros]);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      const result = await batchUploadMacros(parsedMacros, "booster_import");
      const { summary, results: macroResults } = result;

      const failedMacros = (macroResults || []).filter(
        (r) => r.status === "error" || r.status === "invalid"
      );
      const duplicateMacros = (macroResults || []).filter(
        (r) => r.status === "duplicate"
      );

      if (!result.ok) {
        toast.error("One or more batch requests failed.");
      } else {
        if (summary.created > 0) {
          toast.success(
            `${summary.created}/${summary.requested} dot phrase${summary.created !== 1 ? "s" : ""} imported successfully.`
          );
        }
        if (summary.duplicate > 0) {
          const dupeNames = duplicateMacros.map((d) => d.dot_phrase).join(", ");
          toast.warning(
            `${summary.duplicate} duplicate${summary.duplicate !== 1 ? "s" : ""} skipped: ${dupeNames}`
          );
        }
        if (failedMacros.length > 0) {
          const failNames = failedMacros.map((f) => `${f.dot_phrase}: ${f.error_message}`).join("; ");
          toast.error(
            `${failedMacros.length} dot phrase${failedMacros.length !== 1 ? "s" : ""} failed — ${failNames}`
          );
        }
        if (summary.created === 0 && summary.duplicate === 0 && failedMacros.length === 0) {
          toast.error("No dot phrases were imported.");
        }
      }
    } catch {
      toast.error("An error occurred while uploading dot phrases.");
    } finally {
      setIsSubmitting(false);
      setParsedMacros([]);
    }
  };


  return (
    <div id="upload_macros_panel">
    <Stack gap={24}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {parseError && (
        <Banner variant="error" showIcon>{parseError}</Banner>
      )}

      <div
        {...getRootProps()}
        style={{
          border: "2px dashed",
          borderColor: isDragActive ? "var(--color-purple-500, #8b5cf6)" : "#d1d5db",
          borderRadius: 8,
          padding: 40,
          textAlign: "center",
          cursor: isSubmitting ? "default" : "pointer",
          backgroundColor: isDragActive ? "var(--color-purple-50, #f5f3ff)" : "transparent",
          transition: "border-color 0.2s, background-color 0.2s",
          opacity: isSubmitting ? 0.6 : 1,
          pointerEvents: isSubmitting ? "none" : "auto",
        }}
      >
        <input {...getInputProps({ "aria-label": "Upload custom dot phrase files" })} />
        {isSubmitting ? (
          <>
            <svg
              style={{ width: 48, height: 48, margin: "0 auto 16px", color: "var(--color-purple-500, #8b5cf6)", animation: "spin 1s linear infinite" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <BodyText>Uploading dot phrases...</BodyText>
            <BodyText size="small" muted>Please wait, this may take a moment.</BodyText>
          </>
        ) : (
          <>
            <svg
              style={{ width: 48, height: 48, margin: "0 auto 16px", color: "#69707C" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            {isDragActive ? (
              <BodyText>Drop files here...</BodyText>
            ) : (
              <>
                <BodyText>Drag & drop your Booster dot phrase folder or an individual file here</BodyText>
                <BodyText size="small" muted style={{ color: "#69707C" }}>Accepts .zip archives or individual .bstr files — or click to browse</BodyText>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
        <Button variant="secondary" onClick={() => setShowManualModal(true)}>
          Add a dot phrase manually
        </Button>
      </div>

      <Modal
        open={showConfirmModal}
        onOpenChange={(isOpen) => {
          setShowConfirmModal(isOpen);
          if (!isOpen) setParsedMacros([]);
        }}
        title="Confirm Import"
        subtitle={`${parsedMacros.length} dot phrase${parsedMacros.length !== 1 ? "s" : ""} ready to import. Review and confirm.`}
        size="large"
      >
        <Stack gap={16}>
          <ScrollableArea maxHeight={384}>
            <Accordion>
              {parsedMacros.map((macro, index) => (
                <AccordionItem
                  key={index}
                  id={`confirm-macro-${index}`}
                  trigger={macro.dot_phrase}
                >
                  <Stack gap={8}>
                    <FieldText
                      label="Keywords"
                      value={macro.keywords}
                      onChange={(e) => {
                        const updated = [...parsedMacros];
                        updated[index] = { ...updated[index], keywords: e.target.value };
                        setParsedMacros(updated);
                      }}
                    />
                    <FieldTextarea
                      label="Content"
                      value={macro.content}
                      onChange={(e) => {
                        const updated = [...parsedMacros];
                        updated[index] = { ...updated[index], content: e.target.value };
                        setParsedMacros(updated);
                      }}
                      rows={3}
                    />
                  </Stack>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollableArea>

          <Flex justifyContent="flex-end" gap={12}>
            <Button variant="secondary" onClick={() => {
              setShowConfirmModal(false);
              setParsedMacros([]);
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmSubmit}>
              Submit {parsedMacros.length} Dot Phrase{parsedMacros.length !== 1 ? "s" : ""}
            </Button>
          </Flex>
        </Stack>
      </Modal>

      <BoosterIssuesModal
        open={showIssuesModal}
        onOpenChange={(isOpen) => {
          setShowIssuesModal(isOpen);
          if (!isOpen) setIssuesMacros([]);
        }}
        macros={issuesMacros}
        onResolved={handleIssuesResolved}
      />

      <Modal
        open={showManualModal}
        onOpenChange={setShowManualModal}
        title="Add Custom Dot Phrases"
        subtitle="Enter the details for the custom dot phrase you would like to add."
        size="large"
      >
        <Form onSubmit={handleManualSubmit} loading={isManualSubmitting}>
          <Stack gap={16}>
            {manualSubmitStatus.message && (
              <Banner
                variant={manualSubmitStatus.type === "success" ? "success" : "error"}
                showIcon
              >
                {manualSubmitStatus.message}
              </Banner>
            )}

            {macroEntries.map((entry, index) => (
              <Stack key={entry.id} gap={12} style={{ padding: 16, border: "1px solid #e5e7eb", borderRadius: 8 }}>
                <Flex justifyContent="space-between" alignItems="center">
                  {macroEntries.length > 1 && (
                    <BodyText><strong>Dot Phrase {index + 1}</strong></BodyText>
                  )}
                  {macroEntries.length > 1 && (
                    <Button
                      variant="text-danger"
                      size="small"
                      onClick={() => removeMacro(entry.id)}
                      aria-label={`Remove dot phrase ${index + 1}`}
                      type="button"
                    >
                      Remove
                    </Button>
                  )}
                </Flex>

                <FieldText
                  label="Dot Phrase"
                  value={entry.dotPhrase}
                  onChange={(e) => handleManualInputChange(entry.id, "dotPhrase", e.target.value)}
                  placeholder="Enter dot phrase"
                  required
                />

                <FieldText
                  label="Keywords"
                  value={entry.keywords}
                  onChange={(e) => handleManualInputChange(entry.id, "keywords", e.target.value)}
                  placeholder="Enter keywords (comma separated)"
                  description="Tip: Our AI uses these keywords to suggest the most relevant dot phrases based on the audio."
                  required
                />

                <FieldTextarea
                  label="Content"
                  value={entry.content}
                  onChange={(e) => handleManualInputChange(entry.id, "content", e.target.value)}
                  placeholder="Enter dot phrase content"
                  rows={3}
                  required
                />
              </Stack>
            ))}

            <Button variant="text" onClick={addAnotherMacro} type="button">
              + Add Another Dot Phrase
            </Button>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="secondary" onClick={() => setShowManualModal(false)} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!isManualFormValid() || isManualSubmitting}
                loading={isManualSubmitting}
              >
                {isManualSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </Stack>
        </Form>
      </Modal>
    </Stack>
    </div>
  );
}